import { createHttpClient } from '@shared/adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal, ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Spinner } from '@shared/components/ui/Spinner';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock3, Image, Mail, Trash2, Upload } from 'lucide-react';
import { DragEvent, KeyboardEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReceiptApiAdapter, ReceiptNote } from '../../adapters/ReceiptApiAdapter';

export interface ReceiptNotesModalRef {
  open: () => void;
  close: () => void;
}

interface ReceiptNotesModalProps {
  entryId: number;
  receiptId: number;
  canManageNotes: boolean;
}

const MAX_NOTE_BODY_LENGTH = 5000;
const MAX_ATTACHMENTS = 10;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png'];
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const getFileExtension = (fileName: string): string => {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || '' : '';
};

const isImageAttachment = (mimeType?: string) => !!mimeType && mimeType.startsWith('image/');

export const ReceiptNotesModal = forwardRef<ReceiptNotesModalRef, ReceiptNotesModalProps>(
  ({ entryId, receiptId, canManageNotes }, ref) => {
    const { t } = useTranslation('merchandiseV3');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const modalRef = useRef<ModalCenterRef>(null);
    const imagePreviewModalRef = useRef<ModalCenterRef>(null);
    const deleteConfirmModalRef = useRef<ConfirmModalRef>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [body, setBody] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const [isCreateBlockOpen, setIsCreateBlockOpen] = useState(false);
    const [hasInitializedCreateBlock, setHasInitializedCreateBlock] = useState(false);
    const [selectedImagePreview, setSelectedImagePreview] = useState<{ url: string; fileName: string } | null>(null);

    const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
    const receiptAdapter = useMemo(() => new ReceiptApiAdapter(httpClient), [httpClient]);

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true);
        setIsCreateBlockOpen(false);
        setHasInitializedCreateBlock(false);
        modalRef.current?.openModal();
      },
      close: () => {
        setIsOpen(false);
        setIsCreateBlockOpen(false);
        setHasInitializedCreateBlock(false);
        modalRef.current?.closeModal();
      },
    }));

    const notesQuery = useQuery({
      queryKey: ['entry-receipt-notes', entryId, receiptId],
      queryFn: () => receiptAdapter.getNotes(entryId, receiptId, { page: 1, per_page: 100 }),
      enabled: isOpen && entryId > 0 && receiptId > 0,
      staleTime: 0,
    });

    const createNoteMutation = useMutation({
      mutationFn: (payload: { body?: string; attachments?: File[] }) =>
        receiptAdapter.createNote(entryId, receiptId, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['entry-receipt-notes', entryId, receiptId] });
      },
    });

    const deleteNoteMutation = useMutation({
      mutationFn: (noteId: number) => receiptAdapter.deleteNote(entryId, receiptId, noteId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['entry-receipt-notes', entryId, receiptId] });
      },
    });

    const notes = useMemo<ReceiptNote[]>(() => {
      const rows = notesQuery.data?.data || [];
      return [...rows].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }, [notesQuery.data?.data]);

    useEffect(() => {
      if (!isOpen || hasInitializedCreateBlock || notesQuery.isLoading) return;

      // Si el histórico falla (problema backend), abrir directamente el bloque de creación.
      if (notesQuery.isError) {
        setIsCreateBlockOpen(true);
        setHasInitializedCreateBlock(true);
        return;
      }

      // Si carga correctamente: abrir bloque cuando no hay notas, listar cuando sí hay.
      if (notesQuery.isSuccess) {
        const notesCount = notesQuery.data?.data?.length || 0;
        setIsCreateBlockOpen(notesCount === 0);
        setHasInitializedCreateBlock(true);
      }
    }, [
      isOpen,
      hasInitializedCreateBlock,
      notesQuery.isLoading,
      notesQuery.isError,
      notesQuery.isSuccess,
      notesQuery.data,
    ]);

    const validateAttachments = (files: File[]) => {
      if (files.length > MAX_ATTACHMENTS) {
        return t('receive.notes.errors.max_attachments', 'Puedes adjuntar hasta 10 imágenes por nota');
      }

      for (const file of files) {
        const extension = getFileExtension(file.name);
        const validType = ACCEPTED_MIME_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.includes(extension);

        if (!validType) {
          return t('receive.notes.errors.invalid_file_type', 'Solo se permiten archivos JPG, JPEG o PNG');
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          return t('receive.notes.errors.invalid_file_size', 'Cada archivo debe pesar como máximo 5MB');
        }
      }

      return null;
    };

    const handleFileSelection = (files: FileList | null) => {
      if (!files) return;
      const selectedFiles = Array.from(files);
      const merged = [...attachments, ...selectedFiles].slice(0, MAX_ATTACHMENTS);
      const validationError = validateAttachments(merged);

      if (validationError) {
        toast({
          title: t('toasts.error.title', 'Error'),
          description: validationError,
          variant: 'destructive',
        });
        return;
      }

      setAttachments(merged);
    };

    const handleRemoveAttachment = (index: number) => {
      setAttachments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    };

    const handleDropZoneClick = () => {
      if (!canManageNotes || createNoteMutation.isPending) return;
      fileInputRef.current?.click();
    };

    const handleDropZoneKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleDropZoneClick();
      }
    };

    const handleDropZoneDragOver = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!canManageNotes || createNoteMutation.isPending) return;
      setIsDraggingFiles(true);
    };

    const handleDropZoneDragLeave = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingFiles(false);
    };

    const handleDropZoneDrop = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingFiles(false);
      if (!canManageNotes || createNoteMutation.isPending) return;
      handleFileSelection(event.dataTransfer.files);
    };

    const handleCreateNote = async () => {
      const trimmedBody = body.trim();
      if (!trimmedBody && attachments.length === 0) {
        toast({
          title: t('toasts.error.title', 'Error'),
          description: t(
            'receive.notes.errors.empty_note',
            'Debes ingresar un comentario o adjuntar al menos una imagen',
          ),
          variant: 'destructive',
        });
        return;
      }

      if (trimmedBody.length > MAX_NOTE_BODY_LENGTH) {
        toast({
          title: t('toasts.error.title', 'Error'),
          description: t('receive.notes.errors.body_too_long', 'El comentario no puede superar los 5000 caracteres'),
          variant: 'destructive',
        });
        return;
      }

      const attachmentsError = validateAttachments(attachments);
      if (attachmentsError) {
        toast({
          title: t('toasts.error.title', 'Error'),
          description: attachmentsError,
          variant: 'destructive',
        });
        return;
      }

      try {
        await createNoteMutation.mutateAsync({
          ...(trimmedBody ? { body: trimmedBody } : {}),
          ...(attachments.length > 0 ? { attachments } : {}),
        });

        setBody('');
        setAttachments([]);
        setIsCreateBlockOpen(false);
        toast({
          title: t('toasts.success.title', 'Éxito'),
          description: t('receive.notes.success.created', 'Nota registrada correctamente'),
        });
      } catch (error: any) {
        toast({
          title: t('toasts.error.title', 'Error'),
          description:
            error?.response?.data?.message || error?.message || t('toasts.error.unexpected', 'Error inesperado'),
          variant: 'destructive',
        });
      }
    };

    const handleDeleteNote = (noteId: number) => {
      deleteConfirmModalRef.current?.open(
        async () => {
          try {
            await deleteNoteMutation.mutateAsync(noteId);
            toast({
              title: t('toasts.success.title', 'Éxito'),
              description: t('receive.notes.success.deleted', 'Nota eliminada correctamente'),
            });
          } catch (error: any) {
            toast({
              title: t('toasts.error.title', 'Error'),
              description:
                error?.response?.data?.message || error?.message || t('toasts.error.unexpected', 'Error inesperado'),
              variant: 'destructive',
            });
          }
        },
        undefined,
        t('receive.notes.confirm_delete_title', 'Eliminar nota'),
        t('receive.notes.delete_confirm', '¿Seguro que deseas eliminar esta nota? Esta acción no se puede deshacer.'),
        t('receive.notes.confirm_delete_button', 'Eliminar'),
        t('receive.notes.cancel', 'Cancelar'),
      );
    };

    const handleOpenImagePreview = (url: string, fileName: string) => {
      setSelectedImagePreview({ url, fileName });
      imagePreviewModalRef.current?.openModal();
    };

    const handleCancelCreate = () => {
      const hasNotesInHistory = notes.length > 0;
      const shouldCloseModal = notesQuery.isError || !hasNotesInHistory;

      // Si no hay historial o falló el listado, cancelar debe cerrar la modal completa.
      if (shouldCloseModal) {
        setIsCreateBlockOpen(false);
        setBody('');
        setAttachments([]);
        setIsOpen(false);
        modalRef.current?.closeModal();
        return;
      }

      // Si hay historial, cancelar solo cierra el bloque de creación.
      setIsCreateBlockOpen(false);
      setBody('');
      setAttachments([]);
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('receive.notes.modal_title', 'Notas de recepción')}
        width="lg"
        type="default"
        classNameBody="max-h-[80vh]"
        onCloseModal={() => setIsOpen(false)}
      >
        <div className="max-h-[70vh] flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {notesQuery.isLoading && (
              <div className="rounded-md px-4 py-3 text-sm text-complementary-text text-center">
                <Spinner size="sm" className="py-2" />
                <p>{t('receive.notes.loading', 'Cargando notas...')}</p>
              </div>
            )}

            {!isCreateBlockOpen && !notesQuery.isError && notes.length > 0 && (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div key={note.id} className="space-y-3 rounded-lg border border-card-border bg-card-bg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-title">{note.author?.name || '-'}</p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-complementary-text">
                          <span className="inline-flex items-center gap-1">
                            <Clock3 className="w-3 h-3" />
                            {formatDateTime(note.created_at)}
                          </span>
                          {note.author?.email && (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {note.author.email}
                            </span>
                          )}
                        </div>
                      </div>
                      {canManageNotes && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={deleteNoteMutation.isPending}
                          data-testid={`receipt-note-delete-button-${note.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-error" />
                        </Button>
                      )}
                    </div>

                    {note.body && <p className="text-sm text-text whitespace-pre-wrap">{note.body}</p>}

                    {note.attachments?.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[...note.attachments]
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map((attachment) => (
                            <div
                              key={attachment.id}
                              className="group rounded-md border border-card-border p-2 hover:bg-hover-press"
                            >
                              {isImageAttachment(attachment.mime_type) ? (
                                <button
                                  type="button"
                                  className="w-full text-left"
                                  onClick={() => handleOpenImagePreview(attachment.url, attachment.file_name)}
                                  data-testid={`receipt-note-preview-image-${attachment.id}`}
                                >
                                  <img
                                    src={attachment.url}
                                    alt={attachment.file_name}
                                    className="h-20 w-full rounded object-cover"
                                  />
                                </button>
                              ) : (
                                <a href={attachment.url} target="_blank" rel="noreferrer" className="block">
                                  <div className="flex h-20 w-full items-center justify-center rounded bg-body-bg">
                                    <Image className="w-4 h-4 text-icons" />
                                  </div>
                                </a>
                              )}
                              <p className="mt-1 truncate text-xs text-complementary-text">{attachment.file_name}</p>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Formulario para crear nota */}
            {isCreateBlockOpen && (
              <div className="space-y-3 rounded-lg border border-card-border bg-card-bg p-4">
                <h3 className="text-base font-bold text-title">
                  {t('receive.notes.new_note_title', 'Agregar nueva nota')}
                </h3>

                <TextArea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t('receive.notes.body_placeholder', 'Escribe una nota para esta recepción...')}
                  rows={4}
                  maxLength={MAX_NOTE_BODY_LENGTH}
                  data-testid="receipt-note-body-input"
                  disabled={!canManageNotes || createNoteMutation.isPending}
                />

                <div
                  className={`rounded-md border border-dashed p-4 transition-colors ${
                    isDraggingFiles ? 'border-primary bg-hover-press' : 'border-card-border bg-body-bg'
                  } ${!canManageNotes || createNoteMutation.isPending ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={handleDropZoneClick}
                  onKeyDown={handleDropZoneKeyDown}
                  onDragOver={handleDropZoneDragOver}
                  onDragLeave={handleDropZoneDragLeave}
                  onDrop={handleDropZoneDrop}
                  role="button"
                  tabIndex={0}
                  data-testid="receipt-note-dropzone"
                  aria-disabled={!canManageNotes || createNoteMutation.isPending}
                >
                  <input
                    type="file"
                    id="receipt-note-attachments"
                    className="hidden"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    multiple
                    onChange={(e) => handleFileSelection(e.target.files)}
                    disabled={!canManageNotes || createNoteMutation.isPending}
                    ref={fileInputRef}
                  />
                  <div
                    className="flex flex-col items-center justify-center gap-2 text-center"
                    data-testid="receipt-note-upload-button"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span className="text-sm text-text">
                      {t('receive.notes.upload_dropzone', 'Arrastra imágenes aquí o haz click para seleccionarlas')}
                    </span>
                    <span className="text-xs text-complementary-text">
                      {attachments.length}/{MAX_ATTACHMENTS}
                    </span>
                  </div>
                </div>

                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between rounded-md border border-card-border px-3 py-2"
                      >
                        <span className="truncate text-sm text-text">{file.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveAttachment(index)}
                          data-testid={`receipt-note-remove-attachment-${index}`}
                          disabled={!canManageNotes || createNoteMutation.isPending}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-error" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {!notesQuery.isLoading && (
            <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-card-border bg-body-bg">
              {isCreateBlockOpen ? (
                <>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleCancelCreate}
                    disabled={createNoteMutation.isPending}
                    data-testid="receipt-note-cancel-button"
                  >
                    {t('receive.notes.cancel', 'Cancelar')}
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleCreateNote}
                    disabled={!canManageNotes || createNoteMutation.isPending}
                    data-testid="receipt-note-create-button"
                  >
                    {createNoteMutation.isPending
                      ? t('receive.notes.creating', 'Guardando...')
                      : t('receive.notes.create', 'Guardar nota')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setIsCreateBlockOpen(true)}
                  disabled={!canManageNotes || createNoteMutation.isPending}
                  data-testid="toggle-create-note-button"
                >
                  {t('receive.notes.open_create', 'Agregar nueva nota')}
                </Button>
              )}
            </div>
          )}
        </div>
        <ModalCenter
          ref={imagePreviewModalRef}
          title={selectedImagePreview?.fileName || t('receive.notes.image_preview_title', 'Vista de imagen')}
          type="default"
          width="lg"
        >
          <div className="flex items-center justify-center">
            {selectedImagePreview && (
              <img
                src={selectedImagePreview.url}
                alt={selectedImagePreview.fileName}
                className="max-h-[70vh] w-auto max-w-full rounded-md object-contain"
              />
            )}
          </div>
        </ModalCenter>
        <ConfirmModal ref={deleteConfirmModalRef} />
      </ModalCenter>
    );
  },
);

ReceiptNotesModal.displayName = 'ReceiptNotesModal';
